import { useQuery } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { computed } from "vue";
import { LOR_FORM_PATH } from "@/components/MyStudents/Submissions/constants";
import { collectQuestionLabels, isLorTodo } from "@/components/MyStudents/Submissions/letterOfRecommendation";
import type { FormioFormSchema, FormioSubmission } from "@/components/MyStudents/Submissions/types";
import { hasTodoViewSubmissionPermission } from "@/composables/authorization.js";
import formService from "@/services/form.js";

export const LOR_RESPONSES_QUERY_KEY_PREFIX = "lorRecommenderResponses";
export const LOR_SCHEMA_QUERY_KEY_PREFIX = "lorFormSchema";

const SCHEMA_STALE_TIME_MS = 60 * 60 * 1000;

export const useRecommenderResponses = (applicationId: Ref<string | undefined>, todoId: Ref<string | undefined>) => {
  const enabled = computed(
    () => isLorTodo(todoId.value) && !!applicationId.value && hasTodoViewSubmissionPermission.value,
  );

  const {
    data: reportData,
    isLoading: isReportLoading,
    isError,
  } = useQuery({
    queryKey: [LOR_RESPONSES_QUERY_KEY_PREFIX, applicationId],
    queryFn: async (): Promise<FormioSubmission[]> => {
      const report = await formService.getSubmissionReport({
        applicationIds: [applicationId.value],
        formPaths: [LOR_FORM_PATH],
      });
      return Array.isArray(report) ? report : [];
    },
    enabled,
  });

  // A missing schema only costs us human-readable question text, so it never fails the section.
  const { data: schemaData, isLoading: isSchemaLoading } = useQuery({
    queryKey: [LOR_SCHEMA_QUERY_KEY_PREFIX],
    queryFn: async (): Promise<FormioFormSchema> => await formService.getSchema(LOR_FORM_PATH),
    enabled,
    staleTime: SCHEMA_STALE_TIME_MS,
  });

  const responses = computed<FormioSubmission[]>(() => reportData.value ?? []);
  const questionLabels = computed<Record<string, string>>(() => collectQuestionLabels(schemaData.value?.components));
  const isLoading = computed<boolean>(() => isReportLoading.value || isSchemaLoading.value);

  return {
    responses,
    questionLabels,
    isLoading,
    isError,
  };
};
